import {
	LoadingOutlined,
	MinusCircleOutlined,
	PlusOutlined,
	ExclamationCircleOutlined,
} from '@ant-design/icons'
import {
	Button,
	Descriptions,
	Form,
	Image,
	Input,
	message,
	Modal,
	Select,
	Spin,
	Typography,
	Upload,
	Card,
	Space,
	Alert,
} from 'antd'
import ImgCrop from 'antd-img-crop'
import upload, { FileUpload } from 'api/file/upload'
import {
	threeSixtyDialogCategories,
	whatsAppCloudCategories,
} from 'data/vendorCategories'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { ValidateErrorEntity } from 'rc-field-form/lib/interface'
import { UploadRequestOption } from 'rc-upload/lib/interface'
import React, { useEffect, useState } from 'react'
import {
	editClientWhatsAppDetailsAction,
	getClientWhatsAppDetailsAction,
} from 'slices/clientWhatsAppDetails'
import { getSingleClientAction } from 'slices/singleClient'
import { ClientWhatsAppDetails, VendorType } from 'types'
import handleAxiosError from 'utils/handleAxiosError'
import handleFormError from 'utils/handleFormError'
import WabaManagement from 'api/clients/wabaManagement'

const { Title } = Typography
const { useForm, Item } = Form

interface PropTypes {
	clientId: string
	vendorType: VendorType | undefined
}

type FormValues = ClientWhatsAppDetails

const UpdateWhatsAppDetailsForm: React.FC<PropTypes> = ({
	clientId,
	vendorType,
}) => {
	const [form] = useForm<ClientWhatsAppDetails>()

	const {
		loading: clientWhatsAppDetailsLoading,
		details,
		updating,
	} = useAppSelector(state => state.clientWhatsAppDetails)
	const { clientProfile } = useAppSelector(state => state.singleClient)
	const dispatch = useAppDispatch()
	const [deregistering, setDeregistering] = useState(false)

	const getProfile = () => {
		dispatch(getClientWhatsAppDetailsAction(clientId))
	}

	const getInitialValues = (): FormValues => {
		const websites = details?.websites || []

		return {
			profile_picture_url: details?.profile_picture_url || '',
			verified_name: details?.verified_name || 'None',
			code_verification_status: details?.code_verification_status || '',
			display_phone_number: details?.display_phone_number || '',
			quality_rating: details?.quality_rating || '',
			address: details?.address || '',
			description: details?.description || '',
			email: details?.email || '',
			vertical: details?.vertical || null,
			websites: websites.length
				? websites
				: [''] /** 'Cause of atleast 1 website */,
		}
	}

	const updateFormFields = () => {
		if (details) form.setFieldsValue(getInitialValues())
	}
	useEffect(updateFormFields, [details])

	useEffect(getProfile, [clientId])

	/**
	 * Update the Client Profile details
	 * @param body : get values on form submission to update client profile details
	 */
	const onFinish = (body: FormValues) => {
		dispatch(
			editClientWhatsAppDetailsAction({
				...body,
				id: clientId,
			})
		)
	}
	const onFinishFailed = (
		errorInfo: ValidateErrorEntity<ClientWhatsAppDetails>
	) => {
		handleFormError(errorInfo)
	}

	const [uploadError, setUploadError] = useState<boolean>(false)
	const [loading, setLoading] = useState<boolean>(false)

	const beforeUpload = (file: File) => {
		const isImage = file.type === 'image/png' || file.type === 'image/jpeg'
		if (!isImage) {
			message.error(`Only Image file accepted`)
			setUploadError(true)
		} else if (file.size > 1024 * 1024 * 1) {
			message.error('Image must be less than 1MB')
			setUploadError(true)
			return false
		}
		setUploadError(false)
		return isImage || Upload.LIST_IGNORE
	}

	/**
	 * Upload file function
	 */
	const uploadFile = async (options: UploadRequestOption<FileUpload>) => {
		try {
			setLoading(true)
			const { url } = await upload(options)
			form.setFieldsValue({
				profile_picture_url: url,
			})
			setLoading(false)
		} catch (error: any) {
			setLoading(false)
			handleAxiosError(error)
		}
	}

	const handleDeregisterCoexistence = () => {
		Modal.confirm({
			title: 'Disconnect from Coexistence',
			icon: <ExclamationCircleOutlined />,
			content:
				'This will disconnect your account from WhatsApp Business coexistence mode. You will lose access to coexistence features like contact sync and history sync. You can reconnect later if needed. Are you sure you want to continue?',
			okText: 'Disconnect',
			okType: 'danger',
			cancelText: 'Cancel',
			onOk: async () => {
				setDeregistering(true)
				try {
					await WabaManagement.deregisterCoexistence()
					message.success('Disconnected from coexistence successfully!')
					// Refresh client profile to update coexistence status
					if (clientId) {
						dispatch(getSingleClientAction(clientId))
					}
				} catch (error) {
					handleAxiosError(error as any)
				} finally {
					setDeregistering(false)
				}
			},
		})
	}

	const isCoexistenceEnabled =
		clientProfile?.coexistenceEnabled || clientProfile?.isCoexistenceOnboarding

	return (
		<>
			<div
				className='head-align-flex'
				style={{ paddingBottom: '1rem', marginTop: '1rem' }}
			>
				<div className='Title-Head'>
					<Title level={5}>WhatsApp Details</Title>
				</div>
			</div>
			<h3>Current Logo:</h3>
			<Image
				src={details?.profile_picture_url || ''}
				style={{
					maxHeight: 150,
				}}
			/>
			<Spin spinning={clientWhatsAppDetailsLoading}>
				<Form
					form={form}
					name='updateWhatsAppDetailsForm'
					onFinish={onFinish}
					onFinishFailed={onFinishFailed}
					autoComplete='off'
					layout='vertical'
					initialValues={getInitialValues}
					wrapperCol={{ span: 10 }}
				>
					<Item
						label='Upload New Logo'
						name='profile_picture_url'
						// rules={[{ required: true, message: 'Please upload a Logo' }]}
						extra='Recommended resolution is 512*512 with file size less than 1MB,
            keep visual elements centered'
					>
						<ImgCrop modalTitle='Crop Image'>
							<Upload
								name='profile_picture_url'
								maxCount={1}
								beforeUpload={beforeUpload}
								showUploadList={!uploadError}
								multiple={false}
								customRequest={uploadFile}
								listType='picture-card'
								accept='image/*'
								onRemove={() =>
									form.setFieldsValue({
										profile_picture_url: null,
									})
								}
							>
								<div>
									{loading ? <LoadingOutlined /> : <PlusOutlined />}
									<div style={{ marginTop: 8 }}>Upload</div>
								</div>
							</Upload>
						</ImgCrop>
					</Item>
					<Descriptions>
						<Descriptions.Item
							label='Display name'
							labelStyle={{ fontWeight: 'bold' }}
						>
							{details?.verified_name || 'None'}
						</Descriptions.Item>
					</Descriptions>
					<Item
						name='description'
						label='Description'
						rules={[
							{
								required: true,
								message: 'Please provide a description',
							},
						]}
					>
						<Input.TextArea
							maxLength={vendorType === 'THREE_SIXTY_DIALOG' ? 256 : 512}
							showCount
						/>
					</Item>
					<Item
						name='vertical'
						label='Category'
						rules={[
							{
								required: true,
								message: 'Please select category',
							},
						]}
					>
						<Select
							placeholder='Select category'
							showSearch
							optionFilterProp='label'
							options={
								vendorType === 'THREE_SIXTY_DIALOG'
									? threeSixtyDialogCategories
									: whatsAppCloudCategories
							}
						/>
					</Item>
					<h1 style={{ marginBottom: '1rem' }}>Contact Information</h1>
					<Item
						name='address'
						label='Address'
						rules={[
							{
								required: true,
								message: 'Please provide address',
							},
						]}
					>
						<Input maxLength={256} showCount />
					</Item>
					<Item
						name='email'
						label='Email'
						rules={[
							{
								required: true,
								message: 'Please provide email',
							},
							{
								pattern:
									/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
								message: 'This email is not valid',
							},
						]}
					>
						<Input maxLength={128} showCount />
					</Item>
					<Form.List
						name='websites'
						rules={[
							{
								validator: async (_, websites): Promise<boolean> => {
									if (!websites || websites.length < 1) {
										return Promise.reject(new Error('At least 1 website'))
									}
									return Promise.resolve(true)
								},
							},
						]}
					>
						{(fields, { add, remove }) => (
							<>
								{fields.map((field, index) => (
									<Form.Item
										wrapperCol={{
											xs: { span: 10 },
											sm: { span: 10 },
										}}
										label={index === 0 ? 'Website' : ''}
										required={false}
										key={field.key}
										className='dynamic-input'
									>
										<Form.Item
											key={field.key}
											name={field.name}
											validateTrigger={['onChange', 'onBlur']}
											rules={[
												{
													required: true,
													whitespace: true,
													message: "Please input website's url or delete this field.",
												},
												{
													pattern:
														/https:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/,
													message: 'Domain is not valid',
												},
											]}
											noStyle
										>
											<Input placeholder='Website url' maxLength={256} showCount />
										</Form.Item>

										{fields.length > 1 && (
											<MinusCircleOutlined onClick={() => remove(field.name)} />
										)}
									</Form.Item>
								))}
								<Form.Item>
									<Button type='dashed' onClick={() => add()} icon={<PlusOutlined />}>
										Add another website
									</Button>
								</Form.Item>
							</>
						)}
					</Form.List>
					<Item>
						<Button type='primary' htmlType='submit' loading={updating}>
							Update
						</Button>
					</Item>
				</Form>
			</Spin>
			{isCoexistenceEnabled && (
				<Card
					title='WhatsApp Business Coexistence'
					style={{ marginTop: '2rem', maxWidth: 600 }}
					headStyle={{ backgroundColor: '#f0f0f0' }}
				>
					<Space direction='vertical' size='middle' style={{ width: '100%' }}>
						<Alert
							message='Coexistence Mode Active'
							description='Your account is connected to WhatsApp Business coexistence mode. This allows you to use both the WhatsApp Business app and Cloud API simultaneously.'
							type='info'
							showIcon
						/>
						<Descriptions column={1} size='small'>
							<Descriptions.Item label='Status'>
								<span style={{ color: '#52c41a', fontWeight: 'bold' }}>Enabled</span>
							</Descriptions.Item>
							{clientProfile?.coexistenceOnboardedAt && (
								<Descriptions.Item label='Onboarded At'>
									{new Date(clientProfile.coexistenceOnboardedAt).toLocaleString()}
								</Descriptions.Item>
							)}
							{clientProfile?.historyShared && (
								<Descriptions.Item label='History Shared'>
									<span style={{ color: '#52c41a' }}>Yes</span>
								</Descriptions.Item>
							)}
						</Descriptions>
						<Button
							type='primary'
							danger
							onClick={handleDeregisterCoexistence}
							loading={deregistering}
							block
						>
							Disconnect from Coexistence
						</Button>
						<p style={{ fontSize: '12px', color: '#666', margin: 0 }}>
							Note: You may also need to disconnect from WhatsApp Business App settings
							(Settings → Account → Business Platform → Disconnect) if you want to
							fully remove coexistence.
						</p>
					</Space>
				</Card>
			)}
		</>
	)
}

export default UpdateWhatsAppDetailsForm
